import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  jobTitlesService,
  type CreateJobTitleData,
  type UpdateJobTitleData,
} from '@/services/jobTitles.service';

// Query key factory
export const jobTitleKeys = {
  all: ['jobTitles'] as const,
  user: (userId: string) => [...jobTitleKeys.all, 'user', userId] as const,
};

// Get user job titles
export function useUserJobTitles(userId?: string) {
  return useQuery({
    queryKey: jobTitleKeys.user(userId!),
    queryFn: () => jobTitlesService.getUserJobTitles(userId!),
    enabled: !!userId,
  });
}

// Create job title
export function useCreateJobTitle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateJobTitleData) => jobTitlesService.createJobTitle(data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: jobTitleKeys.user(variables.userId) });
      // Also invalidate employee profile data
      void queryClient.invalidateQueries({ queryKey: ['employees', 'profile', variables.userId] });
    },
  });
}

// Update job title
export function useUpdateJobTitle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; userId: string; data: UpdateJobTitleData }) =>
      jobTitlesService.updateJobTitle(id, data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: jobTitleKeys.user(variables.userId) });
      // Also invalidate employee profile data
      void queryClient.invalidateQueries({ queryKey: ['employees', 'profile', variables.userId] });
    },
  });
}

// Delete job title
export function useDeleteJobTitle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string; userId: string }) => jobTitlesService.deleteJobTitle(id),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: jobTitleKeys.user(variables.userId) });
      // Also invalidate employee profile data
      void queryClient.invalidateQueries({ queryKey: ['employees', 'profile', variables.userId] });
    },
  });
}
